import React, { useState, useEffect, useCallback } from 'react';
import './RulesPage.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// Super-admin only (backend enforces via role_required('super_admin') on every
// /api/rules endpoint). Cross-practice, like Mission Control: rules attach to
// payers/plans/practices, not to whichever practice happens to be open.
//
// Lifecycle surface mirrors the backend's invariants: drafts are editable,
// active rules are immutable (New Version instead), activation requires a
// replay run + an approver, and a replay that would have changed historically
// PAID claims demands an extra confirmation showing the scary number.

const EMPTY_CONDITION = { subject: 'line.cpt', op: 'eq', value: '' };

function emptyForm() {
  return {
    rule_key: '',
    quantifier: 'claim',
    conditions: [{ ...EMPTY_CONDITION }],
    any_groups: [], // OR-groups: [{ conditions: [...] }, ...] — at least one must hold
    action_type: 'WARN',
    action_field: '',
    action_value: '',
    action_map: '',
    action_reason: '',
    cond_subject: '',
    cond_op: 'eq',
    cond_value: '',
    priority: 0,
    effective_from: '',
    effective_to: '',
    source: '',
    author: 'human',
    notes: '',
  };
}

function condsSummary(conds) {
  return conds
    .map(c => `${c.subject} ${c.op} ${c.value_set ? `[${c.value_set}]` : JSON.stringify(c.value ?? '')}`)
    .join(' AND ');
}

function scopeSummary(rule) {
  const conds = rule.scope?.conditions || [];
  const groups = (rule.scope?.any || []).filter(g => (g.conditions || []).length > 0);
  if (conds.length === 0 && groups.length === 0) return 'all claims';
  const parts = [];
  if (conds.length) parts.push(condsSummary(conds));
  if (groups.length) parts.push('(' + groups.map(g => `(${condsSummary(g.conditions)})`).join(' OR ') + ')');
  return parts.join(' AND ');
}

function actionSummary(rule) {
  const a = rule.action || {};
  if (a.type === 'SET') return `SET ${a.field} = ${JSON.stringify(a.value)}`;
  if (a.type === 'TRANSFORM') return `TRANSFORM ${a.field} ${JSON.stringify(a.map || {})}`;
  if (a.type === 'REQUIRE' || a.type === 'FORBID') {
    const c = a.condition;
    return `${a.type} ${c ? `${c.subject} ${c.op} ${JSON.stringify(c.value ?? c.value_set ?? '')}` : ''} — "${a.reason || ''}"`;
  }
  return `${a.type} — "${a.reason || ''}"`;
}

function ruleToForm(rule) {
  const f = emptyForm();
  f.rule_key = rule.rule_key;
  f.quantifier = rule.scope?.quantifier || 'claim';
  const condToForm = c => ({
    subject: c.subject || '',
    op: c.op || 'eq',
    value: c.value_set != null ? c.value_set : Array.isArray(c.value) ? c.value.join(', ') : (c.value ?? ''),
  });
  f.conditions = (rule.scope?.conditions || []).map(condToForm);
  if (f.conditions.length === 0) f.conditions = [{ ...EMPTY_CONDITION }];
  f.any_groups = (rule.scope?.any || []).map(g => ({ conditions: (g.conditions || []).map(condToForm) }));
  const a = rule.action || {};
  f.action_type = a.type || 'WARN';
  f.action_field = a.field || '';
  f.action_value = a.value != null ? String(a.value) : '';
  f.action_map = a.map ? JSON.stringify(a.map) : '';
  f.action_reason = a.reason || '';
  if (a.condition) {
    f.cond_subject = a.condition.subject || '';
    f.cond_op = a.condition.op || 'eq';
    f.cond_value = a.condition.value_set != null ? a.condition.value_set : String(a.condition.value ?? '');
  }
  f.priority = rule.priority || 0;
  f.effective_from = rule.effective_from || '';
  f.effective_to = rule.effective_to || '';
  f.source = rule.source || '';
  f.author = rule.author || 'human';
  f.notes = rule.notes || '';
  return f;
}

function parseCondValue(op, raw) {
  // in/not_in take comma-separated lists; between takes "lo, hi"; numbers pass
  // through as strings — the engine compares string-tolerantly by design.
  if (op === 'in' || op === 'not_in') return raw.split(',').map(v => v.trim()).filter(Boolean);
  if (op === 'between') {
    const parts = raw.split(',').map(v => v.trim());
    return [parts[0] ?? '', parts[1] ?? ''];
  }
  if (op === 'missing' || op === 'exists') return undefined;
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return raw;
}

function condsToPayload(conds) {
  return conds
    .filter(c => c.subject)
    .map(c => {
      const cond = { subject: c.subject.trim(), op: c.op };
      if (c.op === 'in_set' || c.op === 'not_in_set') cond.value_set = c.value.trim();
      else {
        const v = parseCondValue(c.op, c.value);
        if (v !== undefined) cond.value = v;
      }
      return cond;
    });
}

function formToPayload(f) {
  const conditions = condsToPayload(f.conditions);
  const anyGroups = (f.any_groups || [])
    .map(g => ({ conditions: condsToPayload(g.conditions) }))
    .filter(g => g.conditions.length > 0);
  const scope = { conditions, quantifier: f.quantifier };
  if (anyGroups.length > 0) scope.any = anyGroups;
  const action = { type: f.action_type };
  if (f.action_type === 'SET') {
    action.field = f.action_field.trim();
    action.value = f.action_value;
  } else if (f.action_type === 'TRANSFORM') {
    action.field = f.action_field.trim();
    try { action.map = f.action_map ? JSON.parse(f.action_map) : {}; }
    catch (e) { throw new Error('TRANSFORM map must be valid JSON, e.g. {"95": "GT"}'); }
  } else {
    action.reason = f.action_reason.trim();
    if ((f.action_type === 'REQUIRE' || f.action_type === 'FORBID') && f.cond_subject) {
      const cond = { subject: f.cond_subject.trim(), op: f.cond_op };
      if (f.cond_op === 'in_set' || f.cond_op === 'not_in_set') cond.value_set = f.cond_value.trim();
      else {
        const v = parseCondValue(f.cond_op, f.cond_value);
        if (v !== undefined) cond.value = v;
      }
      action.condition = cond;
    }
  }
  return {
    rule_key: f.rule_key.trim(),
    scope,
    action,
    priority: Number(f.priority) || 0,
    effective_from: f.effective_from || null,
    effective_to: f.effective_to || null,
    source: f.source || null,
    author: f.author,
    notes: f.notes || null,
  };
}

function RulesPage({ practices, isSuperAdmin }) {
  const [tab, setTab] = useState('rules'); // rules | value-sets | payer-nodes | modes
  const [enums, setEnums] = useState(null);
  const [rules, setRules] = useState([]);
  const [valueSets, setValueSets] = useState([]);
  const [payerNodes, setPayerNodes] = useState([]);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);

  // add/edit form state
  const [editing, setEditing] = useState(null); // null | {id?, form}
  const [saving, setSaving] = useState(false);

  // preview/replay practice target
  const [targetPracticeId, setTargetPracticeId] = useState('');
  const [busyRuleId, setBusyRuleId] = useState(null);
  const [previews, setPreviews] = useState({}); // ruleId -> {tested, matched}

  // One condition-list editor, reused for the top-level (ANDed) conditions
  // and for each OR group. `setConds(nextList)` replaces the whole list.
  const renderConditions = (conds, setConds) => {
    const update = (i, patch) => { const next = [...conds]; next[i] = { ...next[i], ...patch }; setConds(next); };
    return conds.map((c, i) => (
      <div className="form-row cond-row" key={i}>
        <select value={enums.subjects.includes(c.subject) ? c.subject : '__custom'}
          onChange={e => update(i, { subject: e.target.value === '__custom' ? '' : e.target.value })}>
          {enums.subjects.map(sub => <option key={sub} value={sub}>{sub}</option>)}
          <option value="__custom">custom…</option>
        </select>
        {!enums.subjects.includes(c.subject) && (
          <input placeholder="subject path" value={c.subject} onChange={e => update(i, { subject: e.target.value })} />
        )}
        <select value={c.op} onChange={e => update(i, { op: e.target.value })}>
          {enums.operators.map(op => <option key={op} value={op}>{op}</option>)}
        </select>
        {(c.op === 'in_set' || c.op === 'not_in_set') ? (
          <select value={c.value} onChange={e => update(i, { value: e.target.value })}>
            <option value="">— pick value set —</option>
            {valueSets.map(vs => <option key={vs.set_key} value={vs.set_key}>{vs.set_key}</option>)}
          </select>
        ) : (c.op === 'missing' || c.op === 'exists') ? null : (
          <input placeholder={c.op === 'in' || c.op === 'not_in' ? 'a, b, c' : c.op === 'between' ? 'low, high' : 'value'}
            value={c.value} onChange={e => update(i, { value: e.target.value })} />
        )}
        <button className="cond-remove" onClick={() => setConds(conds.filter((_, j) => j !== i))}>✕</button>
      </div>
    ));
  };

  const api = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${API_URL}/api/rules${path}`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      ...opts,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error(data.error || `Request failed (${res.status})`), { data });
    return data;
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [e, r, vs, pn] = await Promise.all([
        api('/enums'), api(''), api('/value-sets'), api('/payer-nodes'),
      ]);
      setEnums(e);
      setRules(r.rules);
      setValueSets(vs.value_sets);
      setPayerNodes(pn.payer_nodes);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (!targetPracticeId && practices?.length) {
      const olympus = practices.find(p => p.clearinghouse === 'stedi');
      setTargetPracticeId(String((olympus || practices[0]).id));
    }
  }, [practices, targetPracticeId]);

  const saveRule = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const payload = formToPayload(editing.form);
      if (editing.id) {
        await api(`/${editing.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      } else {
        await api('', { method: 'POST', body: JSON.stringify(payload) });
      }
      setEditing(null);
      await loadAll();
      setMessage({ type: 'success', text: 'Rule saved as draft.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const doAction = async (rule, action) => {
    setBusyRuleId(rule.id);
    setMessage(null);
    try {
      if (action === 'new-version') {
        await api(`/${rule.id}/new-version`, { method: 'POST' });
        setMessage({ type: 'success', text: `Draft v${rule.version + 1} of ${rule.rule_key} created.` });
      } else if (action === 'retire') {
        if (!window.confirm(`Retire ${rule.rule_key} v${rule.version}? It stops running immediately.`)) return;
        await api(`/${rule.id}/retire`, { method: 'POST' });
      } else if (action === 'preview') {
        const data = await api(`/${rule.id}/preview`, { method: 'POST', body: JSON.stringify({ practice_id: Number(targetPracticeId) }) });
        setPreviews(prev => ({ ...prev, [rule.id]: data }));
      } else if (action === 'replay') {
        const data = await api(`/${rule.id}/replay`, { method: 'POST', body: JSON.stringify({ practice_id: Number(targetPracticeId) }) });
        setMessage({
          type: data.changed_paid > 0 ? 'error' : 'success',
          text: `Replay of ${rule.rule_key} v${rule.version}: ${data.claims_tested} tested, ${data.would_change} would change, ${data.changed_paid} were historically PAID.`,
        });
      } else if (action === 'activate') {
        const approvedBy = window.prompt('Activation requires an approver. Approved by:');
        if (!approvedBy) return;
        try {
          await api(`/${rule.id}/activate`, { method: 'POST', body: JSON.stringify({ approved_by: approvedBy }) });
          setMessage({ type: 'success', text: `${rule.rule_key} v${rule.version} is ACTIVE.` });
        } catch (err) {
          if (err.data?.error === 'confirm_changed_paid required') {
            const ok = window.confirm(
              `⚠ Replay shows this rule would have changed ${err.data.changed_paid} historically PAID claim(s) ` +
              `(of ${err.data.would_change} total changes).\n\nActivate anyway?`
            );
            if (ok) {
              await api(`/${rule.id}/activate`, {
                method: 'POST',
                body: JSON.stringify({ approved_by: approvedBy, confirm_changed_paid: true }),
              });
              setMessage({ type: 'success', text: `${rule.rule_key} v${rule.version} is ACTIVE.` });
            }
          } else if (err.data?.error === 'replay required before activation') {
            setMessage({ type: 'error', text: 'Run a replay first — activation is replay-gated.' });
          } else {
            throw err;
          }
        }
      }
      await loadAll();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setBusyRuleId(null);
    }
  };

  if (loading && !enums) return <div className="rules-page"><p>Loading rules…</p></div>;
  if (!enums) return <div className="rules-page"><p className="msg-error">{message?.text || 'Failed to load.'}</p></div>;

  const judgeAction = ['REQUIRE', 'FORBID', 'REJECT', 'WARN', 'HOLD'].includes(editing?.form.action_type);

  return (
    <div className="rules-page">
      <div className="rules-header">
        <h1 className="page-title">Rules</h1>
        <div className="rules-header-actions">
          <label className="rules-target">
            Preview / replay against:{' '}
            <select value={targetPracticeId} onChange={e => setTargetPracticeId(e.target.value)}>
              {(practices || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          {tab === 'rules' && (
            <button className="btn btn-primary" onClick={() => setEditing({ id: null, form: emptyForm() })}>+ New Rule</button>
          )}
        </div>
      </div>

      {message && (
        <div className={`rules-msg ${message.type === 'error' ? 'msg-error' : 'msg-success'}`} onClick={() => setMessage(null)}>
          {message.text}
        </div>
      )}

      <div className="rules-tabs">
        {['rules', 'value-sets', 'payer-nodes'].concat(isSuperAdmin ? ['modes'] : []).map(t => (
          <button key={t} className={`rules-tab${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>
            {t === 'rules' ? 'Rules' : t === 'value-sets' ? 'Value Sets' : t === 'payer-nodes' ? 'Payer Tree' : 'Practice Modes'}
          </button>
        ))}
      </div>

      {tab === 'rules' && (
        <table className="rules-table">
          <thead>
            <tr>
              <th>Key</th><th>V</th><th>Status</th><th>Scope</th><th>Action</th>
              <th>Prio</th><th>Hits 30d</th><th>Last replay</th><th></th>
            </tr>
          </thead>
          <tbody>
            {rules.map(r => (
              <tr key={r.id} className={`rule-status-${r.status}`}>
                <td className="rule-key">{r.rule_key}</td>
                <td>v{r.version}</td>
                <td><span className={`rule-badge rule-badge-${r.status}`}>{r.status}</span></td>
                <td className="rule-scope" title={scopeSummary(r)}>
                  {scopeSummary(r)}
                  {r.scope?.quantifier && r.scope.quantifier !== 'claim' && <span className="rule-quant"> [{r.scope.quantifier}]</span>}
                </td>
                <td className="rule-action" title={actionSummary(r)}>{actionSummary(r)}</td>
                <td>{r.priority}</td>
                <td>{r.hits_30d}</td>
                <td className="rule-replay">
                  {r.last_replay
                    ? <span className={r.last_replay.changed_paid > 0 ? 'replay-scary' : ''}>
                        {r.last_replay.would_change}/{r.last_replay.claims_tested} change{r.last_replay.changed_paid > 0 ? `, ${r.last_replay.changed_paid} PAID` : ''}
                      </span>
                    : '—'}
                  {previews[r.id] && <div className="rule-preview-note">matches {previews[r.id].matched}/{previews[r.id].tested}</div>}
                </td>
                <td className="rule-actions">
                  {r.status === 'draft' && <button disabled={busyRuleId === r.id} onClick={() => setEditing({ id: r.id, form: ruleToForm(r) })}>Edit</button>}
                  {r.status === 'draft' && <button disabled={busyRuleId === r.id} onClick={() => doAction(r, 'preview')}>Preview</button>}
                  {r.status === 'draft' && <button disabled={busyRuleId === r.id} onClick={() => doAction(r, 'replay')}>Replay</button>}
                  {r.status === 'draft' && isSuperAdmin && <button disabled={busyRuleId === r.id} className="btn-activate" onClick={() => doAction(r, 'activate')}>Activate</button>}
                  {r.status === 'active' && <button disabled={busyRuleId === r.id} onClick={() => doAction(r, 'new-version')}>New Version</button>}
                  {r.status !== 'retired' && (r.status === 'draft' || isSuperAdmin) && <button disabled={busyRuleId === r.id} className="btn-retire" onClick={() => doAction(r, 'retire')}>Retire</button>}
                </td>
              </tr>
            ))}
            {rules.length === 0 && <tr><td colSpan="9" className="rules-empty">No rules yet — create the first one.</td></tr>}
          </tbody>
        </table>
      )}

      {tab === 'value-sets' && <ValueSetsTab valueSets={valueSets} api={api} onChanged={loadAll} setMessage={setMessage} />}
      {tab === 'payer-nodes' && <PayerNodesTab nodes={payerNodes} enums={enums} api={api} onChanged={loadAll} setMessage={setMessage} />}
      {tab === 'modes' && <PracticeModesTab practices={practices} enums={enums} api={api} setMessage={setMessage} />}

      {editing && (
        <div className="rules-modal-overlay" onClick={() => !saving && setEditing(null)}>
          <div className="rules-modal" onClick={e => e.stopPropagation()}>
            <h2>{editing.id ? `Edit draft: ${editing.form.rule_key}` : 'New rule (saved as draft)'}</h2>

            <div className="form-row">
              <label>Rule key
                <input value={editing.form.rule_key} disabled={!!editing.id}
                  placeholder="olympus_pos_default"
                  onChange={e => setEditing(s => ({ ...s, form: { ...s.form, rule_key: e.target.value } }))} />
              </label>
              <label>Quantifier
                <select value={editing.form.quantifier}
                  onChange={e => setEditing(s => ({ ...s, form: { ...s.form, quantifier: e.target.value } }))}>
                  {enums.quantifiers.map(q => <option key={q} value={q}>{q}</option>)}
                </select>
              </label>
              <label>Priority
                <input type="number" value={editing.form.priority}
                  onChange={e => setEditing(s => ({ ...s, form: { ...s.form, priority: e.target.value } }))} />
              </label>
            </div>

            <h3>Scope — when does this rule apply? (conditions are ANDed)</h3>
            {renderConditions(editing.form.conditions, conds => setEditing(s => ({ ...s, form: { ...s.form, conditions: conds } })))}
            <button className="btn-add-cond" onClick={() => setEditing(s => ({ ...s, form: { ...s.form, conditions: [...s.form.conditions, { ...EMPTY_CONDITION }] } }))}>+ condition</button>

            {editing.form.any_groups.length > 0 && (
              <h4 className="or-groups-title">…AND at least one of these groups must hold (groups are ORed; conditions inside a group are ANDed)</h4>
            )}
            {editing.form.any_groups.map((g, gi) => (
              <div className="or-group" key={gi}>
                <div className="or-group-header">
                  <span>{gi === 0 ? 'Group 1' : `OR Group ${gi + 1}`}</span>
                  <button className="cond-remove" title="remove group"
                    onClick={() => setEditing(s => ({ ...s, form: { ...s.form, any_groups: s.form.any_groups.filter((_, j) => j !== gi) } }))}>✕ group</button>
                </div>
                {renderConditions(g.conditions, conds => setEditing(s => {
                  const groups = [...s.form.any_groups];
                  groups[gi] = { ...groups[gi], conditions: conds };
                  return { ...s, form: { ...s.form, any_groups: groups } };
                }))}
                <button className="btn-add-cond" onClick={() => setEditing(s => {
                  const groups = [...s.form.any_groups];
                  groups[gi] = { ...groups[gi], conditions: [...groups[gi].conditions, { ...EMPTY_CONDITION }] };
                  return { ...s, form: { ...s.form, any_groups: groups } };
                })}>+ condition</button>
              </div>
            ))}
            <button className="btn-add-cond" onClick={() => setEditing(s => ({ ...s, form: { ...s.form, any_groups: [...s.form.any_groups, { conditions: [{ ...EMPTY_CONDITION }] }] } }))}>+ OR group</button>

            <h3>Action — what does it do?</h3>
            <div className="form-row">
              <label>Type
                <select value={editing.form.action_type}
                  onChange={e => setEditing(s => ({ ...s, form: { ...s.form, action_type: e.target.value } }))}>
                  <optgroup label="build (modifies the claim)">
                    {enums.build_actions.map(a => <option key={a} value={a}>{a}</option>)}
                  </optgroup>
                  <optgroup label="presubmit (judges the claim)">
                    {enums.presubmit_actions.map(a => <option key={a} value={a}>{a}</option>)}
                  </optgroup>
                </select>
              </label>
              {(editing.form.action_type === 'SET' || editing.form.action_type === 'TRANSFORM') && (
                <label>Field
                  <select value={editing.form.action_field}
                    onChange={e => setEditing(s => ({ ...s, form: { ...s.form, action_field: e.target.value } }))}>
                    <option value="">Select field…</option>
                    {(enums.build_targets || []).map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </label>
              )}
              {editing.form.action_type === 'SET' && (
                <label>Value
                  <input value={editing.form.action_value}
                    onChange={e => setEditing(s => ({ ...s, form: { ...s.form, action_value: e.target.value } }))} />
                </label>
              )}
              {editing.form.action_type === 'TRANSFORM' && (
                <label>Map (JSON)
                  <input placeholder='{"95": "GT"}' value={editing.form.action_map}
                    onChange={e => setEditing(s => ({ ...s, form: { ...s.form, action_map: e.target.value } }))} />
                </label>
              )}
            </div>
            {judgeAction && (
              <div className="form-row">
                <label className="wide">Reason (shown to billers on the chip)
                  <input value={editing.form.action_reason}
                    placeholder="No active authorization for this payer"
                    onChange={e => setEditing(s => ({ ...s, form: { ...s.form, action_reason: e.target.value } }))} />
                </label>
              </div>
            )}
            {(editing.form.action_type === 'REQUIRE' || editing.form.action_type === 'FORBID') && (
              <div className="form-row">
                <label>{editing.form.action_type === 'REQUIRE' ? 'Must hold:' : 'Must NOT hold:'}
                  <select value={enums.subjects.includes(editing.form.cond_subject) ? editing.form.cond_subject : ''}
                    onChange={e => setEditing(s => ({ ...s, form: { ...s.form, cond_subject: e.target.value } }))}>
                    <option value="">— subject —</option>
                    {enums.subjects.map(sub => <option key={sub} value={sub}>{sub}</option>)}
                  </select>
                </label>
                <select value={editing.form.cond_op}
                  onChange={e => setEditing(s => ({ ...s, form: { ...s.form, cond_op: e.target.value } }))}>
                  {enums.operators.map(op => <option key={op} value={op}>{op}</option>)}
                </select>
                {!(editing.form.cond_op === 'missing' || editing.form.cond_op === 'exists') && (
                  <input placeholder="value" value={editing.form.cond_value}
                    onChange={e => setEditing(s => ({ ...s, form: { ...s.form, cond_value: e.target.value } }))} />
                )}
              </div>
            )}

            <h3>Metadata</h3>
            <div className="form-row">
              <label>Effective from
                <input type="date" value={editing.form.effective_from}
                  onChange={e => setEditing(s => ({ ...s, form: { ...s.form, effective_from: e.target.value } }))} />
              </label>
              <label>Effective to
                <input type="date" value={editing.form.effective_to}
                  onChange={e => setEditing(s => ({ ...s, form: { ...s.form, effective_to: e.target.value } }))} />
              </label>
              <label>Author
                <select value={editing.form.author}
                  onChange={e => setEditing(s => ({ ...s, form: { ...s.form, author: e.target.value } }))}>
                  {enums.authors.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </label>
            </div>
            <div className="form-row">
              <label className="wide">Source (where this knowledge came from)
                <input placeholder="Olympus SOP §3 / UHC companion guide 2026-05 p.14" value={editing.form.source}
                  onChange={e => setEditing(s => ({ ...s, form: { ...s.form, source: e.target.value } }))} />
              </label>
            </div>
            <div className="form-row">
              <label className="wide">Notes
                <textarea rows="2" value={editing.form.notes}
                  onChange={e => setEditing(s => ({ ...s, form: { ...s.form, notes: e.target.value } }))} />
              </label>
            </div>

            <div className="rules-modal-actions">
              <button className="btn btn-secondary" disabled={saving} onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={saving || !editing.form.rule_key} onClick={saveRule}>
                {saving ? 'Saving…' : 'Save draft'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ValueSetsTab({ valueSets, api, onChanged, setMessage }) {
  const [editKey, setEditKey] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editValues, setEditValues] = useState('');

  const save = async () => {
    try {
      const values = editValues.split(/[\n,]/).map(v => v.trim()).filter(Boolean);
      await api('/value-sets', { method: 'POST', body: JSON.stringify({ set_key: editKey.trim(), description: editDesc, values }) });
      setEditKey(''); setEditDesc(''); setEditValues('');
      setMessage({ type: 'success', text: 'Value set saved.' });
      onChanged();
    } catch (err) { setMessage({ type: 'error', text: err.message }); }
  };

  return (
    <div className="valuesets-tab">
      <table className="rules-table">
        <thead><tr><th>Key</th><th>Description</th><th>Values</th><th>V</th><th></th></tr></thead>
        <tbody>
          {valueSets.map(vs => (
            <tr key={vs.set_key}>
              <td className="rule-key">{vs.set_key}</td>
              <td>{vs.description}</td>
              <td className="vs-values" title={vs.values.join(', ')}>{vs.values.slice(0, 12).join(', ')}{vs.values.length > 12 ? ` … (${vs.values.length})` : ''}</td>
              <td>v{vs.version}</td>
              <td><button onClick={() => { setEditKey(vs.set_key); setEditDesc(vs.description || ''); setEditValues(vs.values.join(', ')); }}>Edit</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="vs-editor">
        <h3>{valueSets.some(v => v.set_key === editKey.trim()) ? `Update ${editKey}` : 'New value set'}</h3>
        <div className="form-row">
          <label>Key <input placeholder="TELEHEALTH_CPTS" value={editKey} onChange={e => setEditKey(e.target.value)} /></label>
          <label className="wide">Description <input value={editDesc} onChange={e => setEditDesc(e.target.value)} /></label>
        </div>
        <label className="wide">Values (comma or newline separated)
          <textarea rows="3" value={editValues} onChange={e => setEditValues(e.target.value)} />
        </label>
        <button className="btn btn-primary" disabled={!editKey.trim()} onClick={save}>Save value set</button>
      </div>
    </div>
  );
}

function PayerNodesTab({ nodes, enums, api, onChanged, setMessage }) {
  const [level, setLevel] = useState('family');
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState('');

  const parents = nodes.filter(n => (level === 'lob' ? n.level === 'family' : level === 'plan' ? n.level === 'lob' || n.level === 'family' : false));

  const save = async () => {
    try {
      await api('/payer-nodes', { method: 'POST', body: JSON.stringify({ level, name: name.trim(), parent_id: parentId ? Number(parentId) : null }) });
      setName(''); setParentId('');
      setMessage({ type: 'success', text: 'Payer node created.' });
      onChanged();
    } catch (err) { setMessage({ type: 'error', text: err.message }); }
  };

  const byId = Object.fromEntries(nodes.map(n => [n.id, n]));
  const path = (n) => {
    const parts = [n.name];
    let cur = n;
    while (cur.parent_id && byId[cur.parent_id]) { cur = byId[cur.parent_id]; parts.unshift(cur.name); }
    return parts.join(' › ');
  };

  return (
    <div className="payernodes-tab">
      <table className="rules-table">
        <thead><tr><th>Level</th><th>Path</th><th>Notes</th></tr></thead>
        <tbody>
          {nodes.map(n => (
            <tr key={n.id}><td><span className={`rule-badge node-${n.level}`}>{n.level}</span></td><td>{path(n)}</td><td>{n.notes}</td></tr>
          ))}
          {nodes.length === 0 && <tr><td colSpan="3" className="rules-empty">No payer nodes yet. Start with families (UnitedHealthcare, Medicare…), then plans under them.</td></tr>}
        </tbody>
      </table>
      <div className="vs-editor">
        <h3>Add node</h3>
        <div className="form-row">
          <label>Level
            <select value={level} onChange={e => { setLevel(e.target.value); setParentId(''); }}>
              {enums.node_levels.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
          <label>Name <input value={name} onChange={e => setName(e.target.value)} placeholder={level === 'family' ? 'UnitedHealthcare' : level === 'lob' ? 'Medicare Advantage' : 'WellMed TX HMO'} /></label>
          {level !== 'family' && (
            <label>Parent
              <select value={parentId} onChange={e => setParentId(e.target.value)}>
                <option value="">— parent —</option>
                {parents.map(p => <option key={p.id} value={p.id}>{p.level}: {p.name}</option>)}
              </select>
            </label>
          )}
        </div>
        <button className="btn btn-primary" disabled={!name.trim() || (level !== 'family' && !parentId)} onClick={save}>Add node</button>
        <p className="pn-hint">Link practice payers to these nodes from Practice Details → payer editor (payer_node). Rules scoped to payer.family/lob/plan only match linked payers.</p>
      </div>
    </div>
  );
}

function PracticeModesTab({ practices, enums, api, setMessage }) {
  const [modes, setModes] = useState(() => Object.fromEntries((practices || []).map(p => [p.id, p.rules_mode || 'off'])));

  const setMode = async (practiceId, mode) => {
    try {
      await api(`/practices/${practiceId}/mode`, { method: 'PUT', body: JSON.stringify({ rules_mode: mode }) });
      setModes(m => ({ ...m, [practiceId]: mode }));
      setMessage({ type: 'success', text: `Rules mode for practice ${practiceId} → ${mode}. (Reload the ledger to see it.)` });
    } catch (err) { setMessage({ type: 'error', text: err.message }); }
  };

  const [evaluating, setEvaluating] = useState(null);
  const evaluateExisting = async (practiceId) => {
    setEvaluating(practiceId);
    try {
      let done = 0;
      let remaining = 1;
      while (remaining > 0) {
        const r = await api(`/practices/${practiceId}/evaluate`, { method: 'POST', body: JSON.stringify({ limit: 200 }) });
        done += r.evaluated;
        remaining = r.remaining;
        if (r.evaluated === 0) break;
      }
      setMessage({ type: 'success', text: `Evaluated ${done} existing claim${done === 1 ? '' : 's'} for practice ${practiceId}.` });
    } catch (err) { setMessage({ type: 'error', text: err.message }); }
    setEvaluating(null);
  };

  return (
    <div className="modes-tab">
      <p className="pn-hint">
        <strong>off</strong>: engine never runs · <strong>advise</strong>: verdicts + receipts only, nothing modified or gated (launch mode) ·{' '}
        <strong>enforce</strong>: build rules modify claims, blocked claims can’t submit, flagged need an override.
      </p>
      <table className="rules-table">
        <thead><tr><th>Practice</th><th>Clearinghouse</th><th>Rules mode</th><th>Existing claims</th></tr></thead>
        <tbody>
          {(practices || []).map(p => (
            <tr key={p.id}>
              <td>{p.name}</td>
              <td>{p.clearinghouse || '—'}</td>
              <td>
                <select value={modes[p.id] || 'off'} onChange={e => setMode(p.id, e.target.value)}>
                  {enums.rules_modes.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </td>
              <td>
                <button className="btn btn-secondary" disabled={(modes[p.id] || 'off') === 'off' || evaluating !== null}
                  title="Compute verdicts for claims created before rules were switched on. Never modifies a claim."
                  onClick={() => evaluateExisting(p.id)}>
                  {evaluating === p.id ? 'Evaluating…' : 'Evaluate unevaluated'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default RulesPage;
